import React, { FC } from 'react';
import _ from 'lodash';

import {
  selectCurrentRaiseHandMsgTT,
  selectCurrentReactionMsgTT,
  selectListenersAllowedToSpeak,
} from 'client/redux/pubnub/selectors';
import { roomInfoLS } from 'client/utils';
import { LS_KEYS } from 'client/redux/pubnub/constants';
import { useAppDispatch, useAppSelector, useTranslation } from 'client/hooks';
import { setIsShowFooterPopUpOverlay } from 'client/redux/ui';
import { selectUserUID } from 'client/redux/client/selectors';
import { deleteMessage, removeListenerFromAllowToSpeakList } from 'client/redux/pubnub/actions';
import { selectRoomId } from 'client/redux/room/selectors';
import { signOut } from 'client/redux/client/actions';
import { OUTPUT_LINK } from 'client/constants';
import { useGAEventCommonProps } from 'client/hooks/ga-event-common-props';
import { analytics } from 'client/utils/analytics';

import PopupWrapper from '../PopupWrapper';
import Button from '../../Button';

import css from './LogoutPopup.module.scss';

type Props = {
  onClose: () => void;
};

const LogoutPopup: FC<Props> = ({ onClose }) => {
  const dispatch = useAppDispatch();
  const currentRaiseHandMsgTT = useAppSelector(selectCurrentRaiseHandMsgTT);
  const currentReactionMsgTT = useAppSelector(selectCurrentReactionMsgTT);
  const roomId = useAppSelector(selectRoomId);
  const userId = useAppSelector(selectUserUID);
  const allowedTosSpeakArray = useAppSelector(selectListenersAllowedToSpeak);
  const [isBusy, setIsBusy] = React.useState(false);
  const { translate } = useTranslation('header');
  const { common } = useGAEventCommonProps();

  const signOutProcess = React.useCallback(async () => {
    if (currentRaiseHandMsgTT) {
      await dispatch(
        deleteMessage({
          channel: roomId,
          timeToken: currentRaiseHandMsgTT,
          onDelete: () => {
            roomInfoLS.setData(roomId, { [LS_KEYS.LS_RAISE_HAND_MSG_TT]: null });
          },
        }),
      );
    }

    if (currentReactionMsgTT) {
      roomInfoLS.setData(roomId, { [LS_KEYS.LS_REACTION_MSG_TT]: null, [LS_KEYS.LS_REACTION_MSG_VALUE]: null });
    }

    await dispatch(signOut());

    analytics.gtag.event('audio_chat_user_interaction', {
      ...common,
      user_left: Date.now(),
    });
  }, [currentReactionMsgTT, roomId, dispatch, currentRaiseHandMsgTT]);

  const onLogoutBtnClick = async () => {
    const lsRoomInfo = roomInfoLS.getData(roomId);
    setIsBusy(true);

    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translate('logoutPopup.underBtn'),
    });

    if (_.findIndex(allowedTosSpeakArray, { id: userId }) >= 0) {
      dispatch(removeListenerFromAllowToSpeakList({ roomId, listenerId: userId }));
    }

    if (lsRoomInfo?.isListenerConnectedToCoversation) {
      roomInfoLS.setData(roomId, { isListenerConnectedToCoversation: null });
    }

    await signOutProcess();

    setIsBusy(false);
    onClose();
  };

  const onExitBtnClick = async () => {
    const lsRoomInfo = roomInfoLS.getData(roomId);
    setIsBusy(true);

    if (_.findIndex(allowedTosSpeakArray, { id: userId }) >= 0) {
      dispatch(removeListenerFromAllowToSpeakList({ roomId, listenerId: userId }));
    }

    if (lsRoomInfo?.isListenerConnectedToCoversation) {
      roomInfoLS.setData(roomId, { isListenerConnectedToCoversation: null });
    }

    await signOutProcess();

    setIsBusy(false);
    onClose();

    analytics.gtag.event('audio_chat_content_click', {
      ...common,
      text_clicked: translate('logoutPopup.blackBtn'),
    });
    window.location.href = OUTPUT_LINK;
  };

  React.useEffect(() => {
    dispatch(setIsShowFooterPopUpOverlay(true));
  }, []);

  return (
    <PopupWrapper onClose={onClose}>
      <>
        <div data-testid="logout-popup-title" className={css.title}>
          <span>{translate('logoutPopup.title')}</span>
        </div>
        <div className={css.subtitle}>
          <span>{translate('logoutPopup.subtitle')}</span>
        </div>
        <div className={css.logoutButtons}>
          <Button disabled={isBusy} label={translate('logoutPopup.blackBtn')} onClickHandler={onExitBtnClick} />
          <Button
            disabled={isBusy}
            isCustom
            isUnderline
            label={translate('logoutPopup.underBtn')}
            onClickHandler={onLogoutBtnClick}
          />
        </div>
      </>
    </PopupWrapper>
  );
};

export default LogoutPopup;
